/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  APPLICATION_LOAD,
  APPLICATION_LOAD_FAIL,
  APPLICATION_LOAD_SUCCESS,
  APPLICATION_SEARCH_SORTING_UPDATE,
  APPLICATIONS_FILTER_COUNTRY_VALUE_REMOVE,
  APPLICATIONS_FILTER_PROVINCE_VALUE_REMOVE,
  APPLICATIONS_FILTER_VALUE_ADD,
  APPLICATIONS_FILTER_VALUE_REMOVE,
  APPLICATIONS_FILTER_VALUE_UPDATE,
  APPLICATIONS_RESET,
  APPLICATIONS_SEARCH,
  APPLICATIONS_SEARCH_FAIL,
  APPLICATIONS_SEARCH_SUCCESS,
  ASSIGN_APPLICATION,
  ASSIGN_APPLICATION_FAIL,
  ASSIGN_APPLICATION_RESET,
  ASSIGN_APPLICATION_SUCCESS,
  BULK_ASSIGN_APPLICATION,
  BULK_ASSIGN_APPLICATION_FAIL,
  BULK_ASSIGN_APPLICATION_RESET,
  BULK_ASSIGN_APPLICATION_SUCCESS,
  DOWNLOAD_APPLICATIONS_TO_CSV,
  DOWNLOAD_APPLICATIONS_TO_CSV_CLEAN,
  DOWNLOAD_APPLICATIONS_TO_CSV_FAIL,
  DOWNLOAD_APPLICATIONS_TO_CSV_SUCCESS,
  FLAG_APPLICATION,
  FLAG_APPLICATION_FAIL,
  FLAG_APPLICATION_RESET,
  FLAG_APPLICATION_SUCCESS,
  GET_ASSIGN_APPLICATION,
  GET_ASSIGN_APPLICATION_FAIL,
  GET_ASSIGN_APPLICATION_SUCCESS,
  GET_AVAILABLE_ASSIGN_APPLICATION,
  GET_AVAILABLE_ASSIGN_APPLICATION_FAIL,
  GET_AVAILABLE_ASSIGN_APPLICATION_SUCCESS,
  SCORE_APPLICATION,
  SCORE_APPLICATION_FAIL,
  SCORE_APPLICATION_RESET,
  SCORE_APPLICATION_SUCCESS,
  UPDATE_APPLICATION_LISTING_CURRENT_PAGE,
  UPDATE_APPLICATION_LISTING_PAGE_SIZE,
  UPDATE_SELECTED_APPLICATION,
  UPDATE_SELECTED_APPLICATION_EMAIL,
} from "Constants/ActionTypes";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IApplication } from "Interfaces/Application/IApplication";
import { IApplicationAssignTechnologyAssignment } from "Interfaces/Application/IApplicationAssign";
import { IApplicationBulkAssignTechnologyAssignment } from "Interfaces/Application/IApplicationBulkAssign";
import { IApplicationGetAssign } from "Interfaces/Application/IApplicationGetAssign";
import { IApplicationGetBulkAssign } from "Interfaces/Application/IApplicationGetBulkAssign";
import { IApplicationListRowData } from "Interfaces/Application/IApplicationListRowData";
import { IFilterValue } from "Interfaces/IFilterValue";

/**
 * Action for when loading an application.
 * @interface
 */
export interface ILoadApplication {
  type: typeof APPLICATION_LOAD;
  selectedApplicationEmail: string;
}

/**
 * Action for when an application is loaded successfully.
 * @interface
 */
export interface ILoadApplicationSuccess {
  type: typeof APPLICATION_LOAD_SUCCESS;
  application: IApplication;
}

/**
 * Action for when loading an application failed.
 * @interface
 */
export interface ILoadApplicationFail {
  type: typeof APPLICATION_LOAD_FAIL;
  loadSelectedApplicationError: string;
}

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IApplicationsFilterValueAdd {
  type: typeof APPLICATIONS_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IApplicationsFilterValueRemove {
  type: typeof APPLICATIONS_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter province value.
 * @interface
 */
export interface IApplicationsFilterProvinceValueRemove {
  type: typeof APPLICATIONS_FILTER_PROVINCE_VALUE_REMOVE;
}

/**
 * Action for when removing selected filter country value.
 * @interface
 */
export interface IApplicationsFilterCountryValueRemove {
  type: typeof APPLICATIONS_FILTER_COUNTRY_VALUE_REMOVE;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IApplicationsFilterValueUpdate {
  type: typeof APPLICATIONS_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action for when searching Applications.
 * @interface
 */
export interface ISearchApplications {
  type: typeof APPLICATIONS_SEARCH;
  requestorEmailAddress: string;
  applicationType: string;
  searchText: string;
  selectedApplicationsFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
  tenant: TenantTypesEnum;
  sortColumn?: string;
  sortDescending?: boolean;
}

/**
 * Action for when Applications were searched successfully.
 * @interface
 */
export interface ISearchApplicationsSuccess {
  type: typeof APPLICATIONS_SEARCH_SUCCESS;
  applications: IApplicationListRowData[];
  searchPage: number;
  pageSize: number;
  filteredCount: number;
}

/**
 * Action for when loading Applications failed.
 * @interface
 */
export interface ISearchApplicationsFail {
  type: typeof APPLICATIONS_SEARCH_FAIL;
  error: string;
}

export interface IResetApplications {
  type: typeof APPLICATIONS_RESET;
}

/**
 * Action for when setting the selected application email
 * @interface
 */
export interface IUpdateSelectedApplicationEmail {
  type: typeof UPDATE_SELECTED_APPLICATION_EMAIL;
  selectedApplicationEmail: string;
}

export interface IGetAssignApplication {
  type: typeof GET_ASSIGN_APPLICATION;
  tenant: TenantTypesEnum;
  applicationFormId: number;
}

export interface IGetAssignApplicationSuccess {
  type: typeof GET_ASSIGN_APPLICATION_SUCCESS;
  assignApplicationInfo: IApplicationGetAssign;
}

export interface IGetAssignApplicationFail {
  type: typeof GET_ASSIGN_APPLICATION_FAIL;
  errorMessage: string;
}

export interface IGetAvailableAssignApplication {
  type: typeof GET_AVAILABLE_ASSIGN_APPLICATION;
  tenant: TenantTypesEnum;
  applicationFormIds: number[];
}
export interface IGetAvailableAssignApplicationSuccess {
  type: typeof GET_AVAILABLE_ASSIGN_APPLICATION_SUCCESS;
  assignAvailableApplicationInfo: IApplicationGetBulkAssign;
}
export interface IGetAvailableAssignApplicationFail {
  type: typeof GET_AVAILABLE_ASSIGN_APPLICATION_FAIL;
  errorMessage: string;
}

/**
 * Action for assigning an application.
 * @interface
 */
export interface IAssignApplication {
  type: typeof ASSIGN_APPLICATION;
  tenant: TenantTypesEnum;
  applicationFormId: number;
  technologyAssignments: IApplicationAssignTechnologyAssignment[];
}

/**
 * Action for success when assigning an application.
 * @interface
 */
export interface IAssignApplicationSuccess {
  type: typeof ASSIGN_APPLICATION_SUCCESS;
}

/**
 * Action for failure when assigning an application.
 * @interface
 */
export interface IAssignApplicationFail {
  type: typeof ASSIGN_APPLICATION_FAIL;
  errorMessage: string;
}

export interface IAssignApplicationReset {
  type: typeof ASSIGN_APPLICATION_RESET;
}

/**
 * Action for Bulk assigning an application.
 * @interface
 */
export interface IBulkAssignApplication {
  type: typeof BULK_ASSIGN_APPLICATION;
  tenant: TenantTypesEnum;
  applicationFormIds: number[];
  technologyAssignments: IApplicationBulkAssignTechnologyAssignment[];
}

/**
 * Action for success when assigning an application.
 * @interface
 */
export interface IBulkAssignApplicationSuccess {
  type: typeof BULK_ASSIGN_APPLICATION_SUCCESS;
}

/**
 * Action for failure when assigning an application.
 * @interface
 */
export interface IBulkAssignApplicationFail {
  type: typeof BULK_ASSIGN_APPLICATION_FAIL;
  errorMessage: string;
}

export interface IBulkAssignApplicationReset {
  type: typeof BULK_ASSIGN_APPLICATION_RESET;
}

/**
 * Action for scoring an application.
 * @interface
 */
export interface IScoreApplication {
  type: typeof SCORE_APPLICATION;
  tenant: TenantTypesEnum;
  applicationFormId: number;
  technologyFocusArea: string;
  score: number;
  justification: string;
}

/**
 * Action for success when scoiring an application.
 * @interface
 */
export interface IScoreApplicationSuccess {
  type: typeof SCORE_APPLICATION_SUCCESS;
}

/**
 * Action for failure when scoring an application.
 * @interface
 */
export interface IScoreApplicationFail {
  type: typeof SCORE_APPLICATION_FAIL;
  errorMessage: string;
}

export interface IScoreApplicationReset {
  type: typeof SCORE_APPLICATION_RESET;
}

/**
 * Action for flagging an application.
 * @interface
 */
export interface IFlagApplication {
  type: typeof FLAG_APPLICATION;
  applicationFormId: number;
  justification: string;
  flagReasonId: string[];
}

/**
 * Action for success when flagging an application.
 * @interface
 */
export interface IFlagApplicationSuccess {
  type: typeof FLAG_APPLICATION_SUCCESS;
}

/**
 * Action for failure when flagging an application.
 * @interface
 */
export interface IFlagApplicationFail {
  type: typeof FLAG_APPLICATION_FAIL;
  errorMessage: string;
}

/**
 * Action for resetting after flagging an application.
 */
export interface IFlagApplicationReset {
  type: typeof FLAG_APPLICATION_RESET;
}

/**
 * Action for when updating application form id.
 * @interface
 */
export interface IUpdateSelectedApplication {
  type: typeof UPDATE_SELECTED_APPLICATION;
  selectedApplication: IApplication;
}

/**
 * Action for when updating listing current page
 * @interface
 */
export interface IUpdateCurrentPage {
  type: typeof UPDATE_APPLICATION_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Action for when updating listing page size
 * @interface
 */
export interface IUpdatePageSize {
  type: typeof UPDATE_APPLICATION_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action for when downloading results to CSV
 * @interface
 */
export interface IDownloadApplicationsToCSV {
  type: typeof DOWNLOAD_APPLICATIONS_TO_CSV;
  searchText: string;
  selectedApplicationsFilterValues: IFilterValue[];
  tenant: TenantTypesEnum;
  requestorEmailAddress: string;
}

/**
 * Action for when downloading results to CSV success
 * @interface
 */
export interface IDownloadApplicationsToCSVSuccess {
  type: typeof DOWNLOAD_APPLICATIONS_TO_CSV_SUCCESS;
  csvFile: string;
}

/**
 * Action for when downloading results to CSV failed
 * @interface
 */
export interface IDownloadApplicationsToCSVFail {
  type: typeof DOWNLOAD_APPLICATIONS_TO_CSV_FAIL;
  errorMessage: string;
}

/**
 * Action for when after download clean the download state.
 * @interface
 */
export interface IDownloadApplicationsToCSVClean {
  type: typeof DOWNLOAD_APPLICATIONS_TO_CSV_CLEAN;
}

/**
 * Action: update user's profile is private flag fail.
 * @interface
 */
export interface IApplicationSearchSortingChange {
  type: typeof APPLICATION_SEARCH_SORTING_UPDATE;
  sortColumn?: string;
  sortDescending?: boolean;
}

/**
 * Action creator for APPLICATION_SEARCH_SORTING_UPDATE.
 * @returns {IApplicationSearchSortingChange}
 */
export function applicationSearchSortingUpdate(
  sortColumn?: string,
  sortDescending?: boolean
): IApplicationSearchSortingChange {
  return {
    type: APPLICATION_SEARCH_SORTING_UPDATE,
    sortColumn,
    sortDescending,
  };
}

/**
 * Action creator for APPLICATION_LOAD.
 */
export function loadApplication(selectedApplicationEmail: string): ApplicationActionTypes {
  return {
    type: APPLICATION_LOAD,
    selectedApplicationEmail,
  };
}

/**
 * Action creator for APPLICATIONS_LOAD_SUCCESS.
 * @param Applications the user profiles successfully loaded.
 */
export function loadApplicationSuccess(application: IApplication): ApplicationActionTypes {
  return {
    type: APPLICATION_LOAD_SUCCESS,
    application,
  };
}

/**
 * Action creator for APPLICATIONS_LOAD_FAIL.
 * @param error the error message from the API.
 */
export function loadApplicationFail(loadSelectedApplicationError: string): ApplicationActionTypes {
  return {
    type: APPLICATION_LOAD_FAIL,
    loadSelectedApplicationError,
  };
}

/**
 * Action creator for APPLICATIONS_FILTER_VALUE_ADD.
 * @param error the error message from the API.
 */
export function addApplicationsFilterValue(filterValue: IFilterValue): ApplicationActionTypes {
  return {
    type: APPLICATIONS_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for DOWNLOAD_APPLICATIONS_TO_CSV.
 * @param searchText the search text.
 * @param selectedApplicationsFilterValues selected applications current filter values.
 * @param tenant download tenant.
 */
export function downloadApplicationsToCSV(
  searchText: string,
  selectedApplicationsFilterValues: IFilterValue[],
  tenant: TenantTypesEnum,
  requestorEmailAddress: string
): ApplicationActionTypes {
  return {
    type: DOWNLOAD_APPLICATIONS_TO_CSV,
    searchText,
    selectedApplicationsFilterValues,
    tenant,
    requestorEmailAddress
  };
}

/**
 * Action creator for DOWNLOAD_APPLICATIONS_TO_CSV_FAIL.
 * @param errorMessage the error message from the API.
 */
export function downloadApplicationsToCSVFail(errorMessage: string): ApplicationActionTypes {
  return {
    type: DOWNLOAD_APPLICATIONS_TO_CSV_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for DOWNLOAD_APPLICATIONS_TO_CSV_SUCCESS.
 * @param csvFile the csv file from the API.
 */
export function downloadApplicationsToCSVSuccess(csvFile: string): ApplicationActionTypes {
  return {
    type: DOWNLOAD_APPLICATIONS_TO_CSV_SUCCESS,
    csvFile,
  };
}

/**
 * Action creator for DOWNLOAD_APPLICATIONS_TO_CSV_CLEAN.
 * @param csvFile the csv file from the API.
 */
export function downloadApplicationsToCSVClean(): ApplicationActionTypes {
  return {
    type: "DOWNLOAD_APPLICATIONS_TO_CSV_CLEAN",
  };
}

/**
 * Action creator for APPLICATIONS_FILTER_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeApplicationsFilterValue(filterValue: IFilterValue): ApplicationActionTypes {
  return {
    type: APPLICATIONS_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for APPLICATIONS_FILTER_PROVINCE_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeApplicationsFilterProvinceValue(): ApplicationActionTypes {
  return {
    type: APPLICATIONS_FILTER_PROVINCE_VALUE_REMOVE,
  };
}

/**
 * Action creator for APPLICATIONS_FILTER_COUNTRY_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeApplicationsFilterCountryValue(): ApplicationActionTypes {
  return {
    type: APPLICATIONS_FILTER_COUNTRY_VALUE_REMOVE,
  };
}

/**
 * Action creator for APPLICATIONS_FILTER_VALUE_UPDATE.
 * @param error the error message from the API.
 */
export function updateApplicationsFilterValue(filterValue: IFilterValue): ApplicationActionTypes {
  return {
    type: APPLICATIONS_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for APPLICATIONS_SEARCH.
 */
export function searchApplications(
  searchText: string,
  selectedApplicationsFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number,
  requestorEmailAddress: string,
  applicationType: string,
  tenant: TenantTypesEnum,
  sortColumn?: string,
  sortDescending?: boolean
): ApplicationActionTypes {
  return {
    type: APPLICATIONS_SEARCH,
    searchText,
    selectedApplicationsFilterValues,
    searchPage,
    pageSize,
    requestorEmailAddress,
    applicationType,
    tenant,
    sortColumn,
    sortDescending,
  };
}

/**
 * Action creator for APPLICATIONS_SEARCH_SUCCESS.
 * @param Applications the applications successfully searched.
 */
export function searchApplicationsSuccess(
  applications: IApplicationListRowData[],
  searchPage: number,
  pageSize: number,
  filteredCount: number
): ApplicationActionTypes {
  return {
    type: APPLICATIONS_SEARCH_SUCCESS,
    applications,
    searchPage,
    pageSize,
    filteredCount,
  };
}

/**
 * Action creator for APPLICATIONS_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchApplicationsFail(error: string): ApplicationActionTypes {
  return {
    type: APPLICATIONS_SEARCH_FAIL,
    error,
  };
}

export function resetApplications(): IResetApplications {
  return {
    type: APPLICATIONS_RESET,
  };
}

/**
 * Action creator for UPDATE_SELECTED_APPLICATION.
 */
export function updateSelectedApplication(
  selectedApplication: IApplication
): ApplicationActionTypes {
  return {
    type: UPDATE_SELECTED_APPLICATION,
    selectedApplication,
  };
}

/**
 * Action creator for UPDATE_SELECTED_APPLICATION_EMAIL.
 */
export function updateSelectedApplicationEmail(
  selectedApplicationEmail: string
): ApplicationActionTypes {
  return {
    type: UPDATE_SELECTED_APPLICATION_EMAIL,
    selectedApplicationEmail,
  };
}

/**
 * Action creator for GET_ASSIGN_APPLICATION.
 * @param applicationFormId the application form id.
 */
export function getAssignApplication(
  applicationFormId: number,
  tenant: TenantTypesEnum
): IGetAssignApplication {
  return {
    type: GET_ASSIGN_APPLICATION,
    applicationFormId,
    tenant,
  };
}

/**
 * Action creator for GET_ASSIGN_APPLICATION_SUCCESS.
 */
export function getAssignApplicationSuccess(
  assignApplicationInfo: IApplicationGetAssign
): IGetAssignApplicationSuccess {
  return {
    type: GET_ASSIGN_APPLICATION_SUCCESS,
    assignApplicationInfo,
  };
}

/**
 * Action creator for GET_ASSIGN_APPLICATION_FAIL.
 */
export function getAssignApplicationFail(errorMessage: string): IGetAssignApplicationFail {
  return {
    type: GET_ASSIGN_APPLICATION_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for GET_AVAILABLE_ASSIGN_APPLICATION.
 * @param applicationFormIds the application form id.
 */
export function getAvailableAssignApplication(
  applicationFormIds: number[],
  tenant: TenantTypesEnum
): IGetAvailableAssignApplication {
  return {
    type: GET_AVAILABLE_ASSIGN_APPLICATION,
    applicationFormIds,
    tenant,
  };
}

/**
 * Action creator for GET_AVAILABLE_ASSIGN_APPLICATION_SUCCESS.
 */
export function getAvailableAssignApplicationSuccess(
  assignAvailableApplicationInfo: IApplicationGetBulkAssign
): IGetAvailableAssignApplicationSuccess {
  return {
    type: GET_AVAILABLE_ASSIGN_APPLICATION_SUCCESS,
    assignAvailableApplicationInfo,
  };
}

/**
 * Action creator for GET_AVAILABLE_ASSIGN_APPLICATION_FAIL.
 */
export function getAvailableAssignApplicationFail(
  errorMessage: string
): IGetAvailableAssignApplicationFail {
  return {
    type: GET_AVAILABLE_ASSIGN_APPLICATION_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for ASSIGN_APPLICATION.
 */
export function assignApplication(
  applicationFormId: number,
  technologyAssignments: IApplicationAssignTechnologyAssignment[],
  tenant: TenantTypesEnum
): IAssignApplication {
  return {
    type: ASSIGN_APPLICATION,
    tenant,
    applicationFormId,
    technologyAssignments,
  };
}

/**
 * Action creator for ASSIGN_APPLICATION_SUCCESS.
 */
export function assignApplicationSuccess(): IAssignApplicationSuccess {
  return {
    type: ASSIGN_APPLICATION_SUCCESS,
  };
}

/**
 * Action creator for ASSIGN_APPLICATION_FAIL.
 */
export function assignApplicationFail(errorMessage: string): IAssignApplicationFail {
  return {
    type: ASSIGN_APPLICATION_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for ASSIGN_APPLICATION_RESET.
 */
export function assignApplicationReset(): IAssignApplicationReset {
  return {
    type: ASSIGN_APPLICATION_RESET,
  };
}

/**
 * Action creator for BULK_ASSIGN_APPLICATION.
 */
export function bulkAssignApplication(
  applicationFormIds: number[],
  technologyAssignments: IApplicationBulkAssignTechnologyAssignment[],
  tenant: TenantTypesEnum
): IBulkAssignApplication {
  return {
    type: BULK_ASSIGN_APPLICATION,
    tenant,
    applicationFormIds,
    technologyAssignments,
  };
}

/**
 * Action creator for BULK_ASSIGN_APPLICATION_SUCCESS.
 */
export function bulkAssignApplicationSuccess(): IBulkAssignApplicationSuccess {
  return {
    type: BULK_ASSIGN_APPLICATION_SUCCESS,
  };
}

/**
 * Action creator for BULK_ASSIGN_APPLICATION_FAIL.
 */
export function bulkAssignApplicationFail(errorMessage: string): IBulkAssignApplicationFail {
  return {
    type: BULK_ASSIGN_APPLICATION_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for BULK_ASSIGN_APPLICATION_RESET.
 */
export function bulkAssignApplicationReset(): IBulkAssignApplicationReset {
  return {
    type: BULK_ASSIGN_APPLICATION_RESET,
  };
}

/**
 * Action creator for SCORE_APPLICATION.
 */
export function scoreApplication(
  applicationFormId: number,
  technologyFocusAreaName: string,
  score: number,
  justification: string,
  tenant: TenantTypesEnum
): IScoreApplication {
  return {
    type: SCORE_APPLICATION,
    applicationFormId,
    technologyFocusArea: technologyFocusAreaName,
    score,
    justification,
    tenant,
  };
}

/**
 * Action creator for SCORE_APPLICATION_SUCCESS.
 */
export function scoreApplicationSuccess(): IScoreApplicationSuccess {
  return {
    type: SCORE_APPLICATION_SUCCESS,
  };
}

/**
 * Action creator for SCORE_APPLICATION_FAIL.
 */
export function scoreApplicationFail(errorMessage: string): IScoreApplicationFail {
  return {
    type: SCORE_APPLICATION_FAIL,
    errorMessage,
  };
}

export function scoreApplicationReset(): IScoreApplicationReset {
  return {
    type: SCORE_APPLICATION_RESET,
  };
}

/**
 * Action creator for FLAG_APPLICATION.
 */
export function flagApplication(
  applicationFormId: number,
  justification: string,
  flagReasonId: string[]
): IFlagApplication {
  return {
    type: FLAG_APPLICATION,
    applicationFormId,
    justification,
    flagReasonId,
  };
}

/**
 * Action creator for FLAG_APPLICATION_SUCCESS.
 */
export function flagApplicationSuccess(): IFlagApplicationSuccess {
  return {
    type: FLAG_APPLICATION_SUCCESS,
  };
}

/**
 * Action creator for FLAG_APPLICATION_FAIL.
 */
export function flagApplicationFail(errorMessage: string): IFlagApplicationFail {
  return {
    type: FLAG_APPLICATION_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for FLAG_APPLICATION_RESET
 */
export function flagApplicationReset(): IFlagApplicationReset {
  return {
    type: FLAG_APPLICATION_RESET,
  };
}

/**
 * Action creator for UPDATE_APPLICATION_LISTING_CURRENT_PAGE.
 */
export function updateCurrentPage(currentPage: number): ApplicationActionTypes {
  return {
    type: UPDATE_APPLICATION_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_APPLICATION_LISTING_PAGE_SIZE.
 */
export function updatePageSize(pageSize: number): ApplicationActionTypes {
  return {
    type: UPDATE_APPLICATION_LISTING_PAGE_SIZE,
    pageSize,
  };
}

export type ApplicationActionTypes =
  | IApplicationsFilterCountryValueRemove
  | IApplicationsFilterProvinceValueRemove
  | IApplicationsFilterValueAdd
  | IApplicationsFilterValueRemove
  | IApplicationsFilterValueUpdate
  | IDownloadApplicationsToCSV
  | IDownloadApplicationsToCSVClean
  | IDownloadApplicationsToCSVFail
  | IDownloadApplicationsToCSVSuccess
  | ILoadApplication
  | ILoadApplicationFail
  | ILoadApplicationSuccess
  | IResetApplications
  | ISearchApplications
  | ISearchApplicationsFail
  | ISearchApplicationsSuccess
  | IUpdateCurrentPage
  | IFlagApplication
  | IFlagApplicationSuccess
  | IFlagApplicationFail
  | IFlagApplicationReset
  | IGetAssignApplication
  | IGetAssignApplicationSuccess
  | IGetAssignApplicationFail
  | IGetAvailableAssignApplication
  | IGetAvailableAssignApplicationSuccess
  | IGetAvailableAssignApplicationFail
  | IBulkAssignApplication
  | IBulkAssignApplicationSuccess
  | IBulkAssignApplicationFail
  | IBulkAssignApplicationReset
  | IAssignApplication
  | IAssignApplicationSuccess
  | IAssignApplicationFail
  | IAssignApplicationReset
  | IScoreApplication
  | IScoreApplicationSuccess
  | IScoreApplicationFail
  | IScoreApplicationReset
  | IUpdatePageSize
  | IUpdateSelectedApplication
  | IUpdateSelectedApplicationEmail
  | IApplicationSearchSortingChange;
